import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { resourcesApi, accessRequestsApi, sshApi, BASE_PATH } from '../services/api';
import { Resource, AccessRequest } from '../types';
import { Play, CheckCircle, XCircle, Copy, Box, Monitor, Plus, Lock, Container, RotateCw } from 'lucide-react';
import { format } from 'date-fns';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table';
import PermissionGate from '../components/PermissionGate';
import ResourceForm from '../components/ResourceForm';
import { usePermissions } from '../hooks/usePermissions';
import { useUser } from '../contexts/UserContext';
import { Link } from 'react-router-dom';
import { Button, Card, Chip, EmptyState, IconButton, SearchField, PaginationBar, TanstackTable, Modal, PageHeader, Select, StatusBadge, StatCard } from '@/components/ds-zta';
import { useUrlState } from '@/lib/useUrlState';
import { ROWS_PER_PAGE_OPTIONS, readPage, readRows } from '@/lib/urlPaging';
import { healthRank, timeValue } from '@/lib/sortValues';
import { toast } from '@/lib/toast';
import { errorMessage } from '@/lib/http';
import { copyWithToast } from '@/lib/copy';
import { BRAND } from '@/brand';

const columnHelper = createColumnHelper<Resource>();

/** Formats a timestamp, or returns "—" when it is missing or unparseable
 *  (date-fns `format` throws "Invalid time value" on an Invalid Date). "—" is
 *  the one no-value token; "Never" means never-expires only (design eval F47). */
const formatDateSafe = (value: string | number | Date | null | undefined, pattern: string) => {
  if (value === null || value === undefined || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : format(d, pattern);
};

// AGENT_FRESHNESS_MS — how recently an agent-based cluster must have been seen to
// still count as reachable. Heartbeats refresh last_seen far more often than this.
const AGENT_FRESHNESS_MS = 3 * 60 * 1000;

// LiveAgent is the per-cluster liveness the Agent Hub reports from its live registry.
interface LiveAgent {
  online: boolean;
  last_seen?: string;
}
// LiveAgentMap: cluster external_id -> hub-reported liveness (undefined = not loaded yet).
type LiveAgentMap = Map<string, LiveAgent>;

// isClusterOnline is the single source of truth for a cluster's reachability, shared
// by the status column and the summary cards so they never disagree.
//
// For agent-based clusters the authoritative signal is the Agent Hub's live registry
// (liveAgents) — the exact same source the Terminal uses to connect. This avoids the
// stale-status bug where the DB's last_seen lags (the hub→admin-api heartbeat write can
// fail while the WebSocket stays up), showing Offline even though Terminal connects fine.
// When the hub status hasn't loaded yet (liveAgents undefined) we fall back to the DB
// last_seen freshness heuristic. Non-agent (kubeconfig) clusters use reported health_status.
const isClusterOnline = (resource: Resource, liveAgents?: LiveAgentMap): boolean => {
  const isAgent = resource.labels?.connectivity === 'agent';
  if (!isAgent) return resource.health_status === 'healthy';
  if (liveAgents) return liveAgents.get(resource.external_id)?.online === true;
  const lastSeen = resource.last_seen ? new Date(resource.last_seen) : null;
  const recentlySeen = !!lastSeen && (Date.now() - lastSeen.getTime()) < AGENT_FRESHNESS_MS;
  return recentlySeen && resource.health_status !== 'unhealthy';
};

interface K8sResourcesTableProps {
  resources: Resource[];
  handleConnect: (resource: Resource) => void;
  handleCLI: (resource: Resource) => void;
  PERMISSIONS: any;
  hasValidApproval: (resourceId: string) => boolean;
  liveAgents?: LiveAgentMap;
  loading: boolean;
  searchTerm: string;
  canCreate: boolean;
  onCreate: () => void;
}

const K8sResourcesTable: React.FC<K8sResourcesTableProps> = ({
  resources,
  handleConnect,
  handleCLI,
  PERMISSIONS,
  hasValidApproval,
  liveAgents,
  loading,
  searchTerm,
  canCreate,
  onCreate,
}) => {
  const columns = useMemo<ColumnDef<Resource, any>[]>(
    () => [
      columnHelper.display({
        id: 'cluster',
        header: 'Cluster',
        meta: { sortable: true, sortValue: (r: Resource) => (r.external_id || '').toLowerCase() },
        cell: ({ row }) => (
          <div className="flex items-center">
            <div className="flex-shrink-0 h-8 w-8">
              <div className="h-8 w-8 rounded-full bg-[var(--zta-tint-info)] flex items-center justify-center">
                <Box className="h-5 w-5 text-[var(--d2d-info)]" />
              </div>
            </div>
            <div className="ml-4 w-[150px]">
              <div className="w-full text-dense font-medium text-[var(--d2d-text-primary)] font-mono tabular-nums">
                {row.original.external_id}
              </div>
              <div className="w-full text-dense text-[var(--d2d-text-muted)]">
                {row.original.endpoint?.cluster_name || 'Kubernetes Cluster'}
              </div>
            </div>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'api_server',
        header: 'API Server',
        meta: { sortable: true, sortValue: (r: Resource) => String(r.endpoint?.api_server ?? '').toLowerCase() },
        cell: ({ row }) => (
          <div className="text-dense text-[var(--d2d-text-primary)] w-[200px] truncate">
            {row.original.endpoint?.api_server || '—'}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'namespace',
        header: 'Namespace',
        meta: { sortable: true, sortValue: (r: Resource) => String(r.endpoint?.namespace || 'default') },
        cell: ({ row }) => (
          <span className="text-dense text-[var(--d2d-text-primary)] font-mono tabular-nums">
            {row.original.endpoint?.namespace || 'default'}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        // Online first, then by stored health.
        meta: { sortable: true, sortValue: (r: Resource) => (isClusterOnline(r, liveAgents) ? -1 : healthRank(r.health_status)) },
        cell: ({ row }) => {
          const isAgent = row.original.labels?.connectivity === 'agent';
          const effectiveHealthy = isClusterOnline(row.original, liveAgents);
          const statusLabel = effectiveHealthy ? 'Online' : isAgent ? 'Offline' : (row.original.health_status || 'Unknown');
          return (
            <div className="flex items-center gap-2">
              <StatusBadge
                value={effectiveHealthy ? 'online' : isAgent ? 'offline' : (row.original.health_status || 'unknown')}
                className="capitalize"
              >
                {statusLabel}
              </StatusBadge>
              {isAgent && (
                <Chip size="sm" variant="neutral">
                  Agent
                </Chip>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'last_seen',
        header: 'Last Seen',
        meta: { sortable: true, sortValue: (r: Resource) => timeValue(liveAgents?.get(r.external_id)?.last_seen || r.last_seen) },
        cell: ({ row }) => {
          // Prefer the hub's live last_seen for agent clusters so it stays consistent
          // with the (live-registry-driven) status; fall back to the DB value.
          const liveSeen = liveAgents?.get(row.original.external_id)?.last_seen;
          const seen = liveSeen || row.original.last_seen;
          return (
            <span className="text-dense text-[var(--d2d-text-muted)]">
              {formatDateSafe(seen, 'MMM d, HH:mm')}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span className="block text-right">Actions</span>,
        cell: ({ row }) => {
          const hasAccess = hasValidApproval(row.original.id);
          const isAgent = row.original.labels?.connectivity === 'agent';

          return (
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center justify-end gap-1">
                {/* Terminal button — only agent-managed clusters have the in-cluster
                    agent PTY needed for a web terminal. Kubeconfig clusters can't. */}
                {isAgent && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleConnect(row.original)}
                    disabled={!hasAccess}
                    title={!hasAccess ? "Access request approval required" : "Open Web Terminal"}
                    leadingIcon={<Monitor className="h-3.5 w-3.5" />}
                  >
                    Terminal
                  </Button>
                )}
                {/* CLI button — for ALL clusters. `iap k8s connect` now works for
                    agent-managed clusters too (tunnels through the agent's SA), so
                    kubeconfig and agent clusters both get CLI access. */}
                <IconButton
                  size="sm"
                  aria-label="CLI"
                  tooltip={!hasAccess ? "Access request approval required" : "Show CLI Commands"}
                  onClick={() => handleCLI(row.original)}
                  disabled={!hasAccess}
                  icon={<Play className="h-4 w-4" />}
                />
              </div>
              {!hasAccess && (
                <Link to="/access-requests" className="text-micro text-[var(--d2d-text-primary)] hover:underline flex items-center gap-1">
                  <Lock className="h-3 w-3 flex-shrink-0" aria-hidden="true" />Request Access
                </Link>
              )}
            </div>
          );
        },
      }),
    ],
    [handleConnect, handleCLI, hasValidApproval, PERMISSIONS, liveAgents]
  );

  const table = useReactTable({
    data: resources,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const empty = searchTerm ? (
    <EmptyState
      icon={<Container />}
      title={`No Kubernetes clusters on this page match "${searchTerm}"`}
      description="Try a different search or clear the role filter."
    />
  ) : (
    <EmptyState
      icon={<Container />}
      title="No Kubernetes clusters yet"
      description={`Add a Kubernetes cluster to access it securely through ${BRAND.name}.`}
      action={
        canCreate ? (
          <Button onClick={onCreate} leadingIcon={<Plus className="h-4 w-4" />}>
            Add Cluster
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <div className="overflow-x-auto">
      <TanstackTable aria-label="Kubernetes clusters" table={table} loading={loading} empty={empty} />
    </div>
  );
};

// CLI Commands Modal Component
interface CLIModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: Resource | null;
}

const CLIModal: React.FC<CLIModalProps> = ({ isOpen, onClose, resource }) => {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  if (!isOpen || !resource) return null;

  // Use external_id: that's what `iap k8s connect` resolves against
  // (GET /resources?type=k8s&external_id=…) and the name shown as the cluster's
  // primary label in the table — using cluster_name here mismatched both.
  const clusterName = resource.external_id;

  const commands = [
    {
      title: `Authenticate with ${BRAND.name}`,
      description: `Log in to ${BRAND.name} using OIDC authentication`,
      command: `./iap auth login`,
    },
    {
      title: 'List available clusters',
      description: 'List all Kubernetes clusters you have access to',
      command: `./iap k8s ls`,
    },
    {
      title: 'Connect (background)',
      description: 'Start a background tunnel to the Kubernetes API server',
      command: `./iap k8s connect ${clusterName} --background`,
    },
    {
      title: 'Connect with custom port (background)',
      description: 'Start a background tunnel on a specific local port',
      command: `./iap k8s connect ${clusterName} --local-port 6444 --background`,
    },
    {
      title: 'Show active tunnels',
      description: 'Display all currently active Kubernetes API tunnels',
      command: `./iap k8s status`,
    },
    {
      title: 'Close tunnel',
      description: 'Close an active tunnel to the cluster',
      command: `./iap k8s logout ${clusterName}`,
    },
  ];

  const kubectlCommands = [
    {
      title: 'Get pods',
      description: 'List pods using the generated kubeconfig',
      command: `kubectl --kubeconfig ~/.iap/k8s/${clusterName}.yaml get pods`,
    },
    {
      title: 'Get namespaces',
      description: 'List all namespaces',
      command: `kubectl --kubeconfig ~/.iap/k8s/${clusterName}.yaml get namespaces`,
    },
    {
      title: 'Set KUBECONFIG',
      description: 'Export KUBECONFIG environment variable',
      command: `export KUBECONFIG=~/.iap/k8s/${clusterName}.yaml`,
    },
  ];

  const copyToClipboard = async (command: string) => {
    if (!(await copyWithToast(command, { success: false }))) return;
    setCopiedCommand(command);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  return (
    <Modal
      dismissOnOverlay={false}
      open
      onClose={onClose}
      title="K8s CLI Commands"
      description={<>Cluster: <span className="font-medium text-[var(--d2d-text-secondary)]">{clusterName}</span></>}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
        <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
          {/* IAP K8s Commands */}
          <div>
            <h4 className="text-sm font-medium text-[var(--d2d-text-primary)] mb-3 flex items-center">
              <Box className="h-4 w-4 mr-2 text-[var(--d2d-info)]" />
              {BRAND.name} K8s Commands
            </h4>
            <div className="space-y-3">
              {commands.map((cmd, index) => (
                <div
                  key={index}
                  className="bg-[var(--d2d-bg-primary)] rounded-lg p-3 border border-[var(--d2d-border)]"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h5 className="text-sm font-medium text-[var(--d2d-text-primary)]">
                        {cmd.title}
                      </h5>
                      <p className="text-xs text-[var(--d2d-text-muted)] mt-0.5">
                        {cmd.description}
                      </p>
                    </div>
                    <Button
                      variant={copiedCommand === cmd.command ? 'ghost' : 'secondary'}
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => copyToClipboard(cmd.command)}
                      leadingIcon={<Copy className="h-3 w-3" />}
                    >
                      {copiedCommand === cmd.command ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                  <pre className="text-sm text-[var(--d2d-text-primary)] bg-[var(--ds-color-bg-subtle)] p-2 rounded overflow-x-auto font-mono">
                    {cmd.command}
                  </pre>
                </div>
              ))}
            </div>
          </div>

          {/* kubectl Commands */}
          <div>
            <h4 className="text-sm font-medium text-[var(--d2d-text-primary)] mb-3 flex items-center">
              <Play className="h-4 w-4 mr-2 text-[var(--d2d-success)]" />
              After Tunnel Starts - kubectl Commands
            </h4>
            <div className="space-y-3">
              {kubectlCommands.map((cmd, index) => (
                <div
                  key={index}
                  className="bg-[var(--d2d-bg-primary)] rounded-lg p-3 border border-[var(--d2d-border)]"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h5 className="text-sm font-medium text-[var(--d2d-text-primary)]">
                        {cmd.title}
                      </h5>
                      <p className="text-xs text-[var(--d2d-text-muted)] mt-0.5">
                        {cmd.description}
                      </p>
                    </div>
                    <Button
                      variant={copiedCommand === cmd.command ? 'ghost' : 'secondary'}
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => copyToClipboard(cmd.command)}
                      leadingIcon={<Copy className="h-3 w-3" />}
                    >
                      {copiedCommand === cmd.command ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                  <pre className="text-sm text-[var(--d2d-text-primary)] bg-[var(--ds-color-bg-subtle)] p-2 rounded overflow-x-auto font-mono">
                    {cmd.command}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-[var(--d2d-border)]">
          <div className="bg-[var(--zta-tint-info)] rounded-lg p-4">
            <h4 className="text-sm font-medium text-[var(--d2d-info)] mb-2">
              Prerequisites
            </h4>
            <ul className="text-xs text-[var(--d2d-info)] space-y-1 list-disc list-inside">
              <li>Authenticate with {BRAND.name}: <code className="bg-[var(--zta-tint-info)] px-1 rounded">iap auth login</code></li>
              <li>Ensure kubectl is installed for full functionality</li>
            </ul>
          </div>
        </div>
    </Modal>
  );
};

export default function K8sResources() {
  // Filters, search and paging live in the URL (design eval F24). Role
  // go to the server, so changing it starts again at page 1 (F7). The
  // list endpoint takes no search param, so the search filters the loaded
  // page and says so.
  const [url, setUrl] = useUrlState({ q: '', role: '', page: '1', rows: '20' });
  const searchTerm = url.q;
  const setSearchTerm = (next: string) => setUrl({ q: next }, { replace: true });
  const selectedRole = url.role;
  const setSelectedRole = (next: string) => setUrl({ role: next, page: null });
  const page = readPage(url.page) - 1; // zero-based below, as before
  const itemsPerPage = readRows(url.rows);
  const setPage = (next: number) => setUrl({ page: next + 1 });
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [showCLIModal, setShowCLIModal] = useState(false);

  const queryClient = useQueryClient();
  const { PERMISSIONS, isAdmin, canManageResources } = usePermissions();
  const [showAddForm, setShowAddForm] = useState(false);
  const { secondaryRoles } = useUser();

  // Get approved access requests for non-admin users
  const { data: myApprovalsData } = useQuery(
    ['my-approvals'],
    () => accessRequestsApi.getMyApprovals(),
    {
      refetchInterval: 30000,
    }
  );

  const myApprovals = myApprovalsData?.data?.items || [];

  // Get K8s resources from database
  const { data: resourcesData, isLoading: resourcesLoading, isFetching: resourcesFetching, isError: resourcesIsError, error: resourcesError, refetch: refetchResources } = useQuery(
    ['resources', 'k8s', selectedRole, page, itemsPerPage],
    () => resourcesApi.listByRole('k8s', itemsPerPage, page * itemsPerPage, selectedRole || undefined),
    { keepPreviousData: true }
  );

  const k8sResources = resourcesData?.data?.resources || [];

  // Live agent liveness from the Agent Hub — the same registry the Terminal connects
  // through. This is the authoritative "is the cluster reachable right now" signal and
  // avoids the stale-DB-last_seen mismatch (Offline in the list but Terminal works).
  // Polled so the status tracks connect/disconnect within the interval. On error the query
  // returns undefined and isClusterOnline falls back to the DB last_seen heuristic.
  const { data: agentStatusData } = useQuery(
    ['k8s-agent-status'],
    () => sshApi.getAgents(),
    { refetchInterval: 15000, staleTime: 10000 }
  );

  // Map of external_id -> hub-reported liveness (undefined until first load, so
  // isClusterOnline can fall back to the DB heuristic while the query is in flight).
  const liveAgents = useMemo<LiveAgentMap | undefined>(() => {
    const agents = agentStatusData?.data?.agents;
    if (!agents) return undefined;
    const map: LiveAgentMap = new Map();
    for (const a of agents as any[]) {
      if (a?.server_id) map.set(a.server_id, { online: a.online === true, last_seen: a.last_seen });
    }
    return map;
  }, [agentStatusData]);

  // Fetch access-status for each K8s resource to check role-based direct access
  // This covers users who have direct access via roles (no access request needed)
  const { data: accessStatusMap } = useQuery(
    ['k8s-access-status', k8sResources.map(r => r.id).join(',')],
    async () => {
      const statusMap: Record<string, boolean> = {};
      await Promise.all(
        k8sResources.map(async (resource) => {
          try {
            const resp = await resourcesApi.getAccessStatus(resource.id);
            statusMap[resource.id] = resp.data.has_access === true;
          } catch {
            statusMap[resource.id] = false;
          }
        })
      );
      return statusMap;
    },
    {
      enabled: k8sResources.length > 0 && !isAdmin(),
      refetchInterval: 30000,
    }
  );

  // Filter resources by search term
  const filteredResources = k8sResources.filter(resource =>
    resource.external_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.endpoint?.cluster_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.endpoint?.api_server?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    Object.values(resource.labels || {}).some(label =>
      String(label).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleWebTerminal = (resource: Resource) => {
    // Check if user has access (admin or has valid approval)
    if (!hasValidApproval(resource.id)) {
      toast.error('Access denied: You need an approved access request with a valid duration to access this resource.');
      return;
    }

    // Open terminal in a new tab. Use external_id — the backend resolves the cluster
    // session by external_id, and it's the cluster's primary label in the table.
    const clusterName = resource.external_id;
    window.open(`${BASE_PATH}/terminal?cluster=${encodeURIComponent(clusterName)}`, '_blank');
  };

  const handleCLI = (resource: Resource) => {
    // Check if user has access (admin or has valid approval)
    if (!hasValidApproval(resource.id)) {
      toast.error('Access denied: You need an approved access request to access this resource.');
      return;
    }

    setSelectedResource(resource);
    setShowCLIModal(true);
  };

  // Check if user has a valid approval for the resource (admins bypass this check)
  const hasValidApproval = (resourceId: string) => {
    // Admin users bypass access request requirements
    if (isAdmin()) {
      return true;
    }

    // Check role-based direct access (from access-status API)
    if (accessStatusMap && accessStatusMap[resourceId]) {
      return true;
    }

    // Check access request approvals
    const approval = myApprovals.find(
      (req: AccessRequest) => req.resource_id === resourceId
    );

    if (!approval) return false;

    // Check if approval status is 'approved' (not pending, expired, or revoked)
    if (approval.status !== 'approved') return false;

    // Double-check expiration time
    if (approval.approved_at && approval.duration_seconds) {
      const approvedTime = new Date(approval.approved_at).getTime();
      const expiryTime = approvedTime + (approval.duration_seconds * 1000);
      if (Date.now() >= expiryTime) return false;
    }

    return true;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div data-tour="kubernetes-header">
        <PageHeader
          title="K8s Resources"
          icon={<Container />}
          description={`Manage Kubernetes clusters and access them securely through ${BRAND.name}.`}
          onRefresh={() => refetchResources()}
          refreshing={resourcesFetching}
          primaryAction={
            canManageResources() ? (
              <Button
                variant="primary"
                data-tour="kubernetes-add"
                onClick={() => setShowAddForm(true)}
                leadingIcon={<Plus className="h-4 w-4" />}
              >
                Add Cluster
              </Button>
            ) : undefined
          }
        />
      </div>

      {showAddForm && (
        <ResourceForm
          initialType="k8s"
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(['resources', 'k8s']);
            refetchResources();
            setShowAddForm(false);
          }}
        />
      )}

      {/* Stats Cards */}
      <div data-tour="kubernetes-stats" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard icon={<Box />} accent="info" label="Total Clusters" value={k8sResources.length} />

        <StatCard icon={<CheckCircle />} accent="success" label="Healthy" value={k8sResources.filter(r => isClusterOnline(r, liveAgents)).length} />

        {/* Not reachable, but exclude clusters in an unconfirmed 'unknown' state
            (e.g. kubeconfig clusters never health-checked) — those are neither. */}
        <StatCard icon={<XCircle />} accent="danger" label="Unhealthy" value={k8sResources.filter(r => !isClusterOnline(r, liveAgents) && r.health_status !== 'unknown').length} />
      </div>

      {/* Search and Filters */}
      <Card className="p-4 overflow-visible" data-tour="kubernetes-search">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <SearchField
              label="Search K8s clusters on this page"
              placeholder="Search K8s clusters on this page..."
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              help={(resourcesData?.data?.total ?? 0) > k8sResources.length ? `Searches the ${k8sResources.length} shown on this page, of ${(resourcesData?.data?.total ?? 0)}.` : undefined}
            />
          </div>
          {secondaryRoles.length > 0 && (
            <div className="sm:w-44">
              <Select
                className="gap-0" /* hidden label: no gap above the field, so it lines up with the search (F46) */
                label={<span className="sr-only">Select Role</span>}
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">Select Role</option>
                {secondaryRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </Card>

      {/* K8s Resources Table */}
      <Card className="overflow-hidden" data-tour="kubernetes-list">
        {resourcesIsError ? (
          <EmptyState
            icon={<Container />}
            title="Failed to load Kubernetes clusters"
            description={errorMessage(resourcesError, 'Something went wrong while loading Kubernetes resources.')}
            action={
              <Button variant="secondary" onClick={() => refetchResources()} leadingIcon={<RotateCw className="h-4 w-4" />}>
                Retry
              </Button>
            }
          />
        ) : (
        <K8sResourcesTable
          resources={filteredResources}
          handleConnect={handleWebTerminal}
          handleCLI={handleCLI}
          PERMISSIONS={PERMISSIONS}
          hasValidApproval={hasValidApproval}
          liveAgents={liveAgents}
          loading={resourcesLoading}
          searchTerm={searchTerm}
          canCreate={canManageResources()}
          onCreate={() => setShowAddForm(true)}
        />
        )}
      </Card>

      {/* Pagination */}
      {resourcesData && resourcesData.data.total > 0 && (
        <div className="mt-4">
          {resourcesData.data.total > 0 && <PaginationBar
            page={page + 1}
            totalRows={resourcesData.data.total}
            rowsPerPage={itemsPerPage}
            onPageChange={(p) => setPage(p - 1)}
            onRowsPerPageChange={(newItemsPerPage) => setUrl({ rows: newItemsPerPage, page: null })}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />}
        </div>
      )}

      {/* CLI Commands Modal */}
      <CLIModal
        isOpen={showCLIModal}
        onClose={() => {
          setShowCLIModal(false);
          setSelectedResource(null);
        }}
        resource={selectedResource}
      />

    </div>
  );
}
